import styled from '@emotion/styled'
import { forwardRef, Ref, useEffect, useRef, useState } from 'react'
import { NavLink, useMatch } from 'react-router-dom'
import { User } from 'api/model/user'
import ProfilePhoto from 'components/ProfilePhoto'
import {
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Popper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import EmailIcon from '@mui/icons-material/Email'
import { ReactComponent as AppIcon } from './app-icon-dark.svg'
import { ReactComponent as JelledText } from './jelled-text-dark.svg'
import Groups3Icon from '@mui/icons-material/Groups3'
import Groups3OutlinedIcon from '@mui/icons-material/Groups3Outlined'
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined'
import { VirtualElement } from '@popperjs/core'
import { useActiveOrganization } from 'hooks/use-organization'
import { useAuthToken } from 'hooks/use-auth-token'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined'
import SettingsIcon from '@mui/icons-material/Settings'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates'
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined'
import AutoGraphIcon from '@mui/icons-material/AutoGraph'
import FirstPageSharpIcon from '@mui/icons-material/FirstPageSharp'
import LastPageSharpIcon from '@mui/icons-material/LastPageSharp'
import { useRecoilState, useRecoilValue } from 'recoil'
import { getColorForDarkMode } from 'utils'
import { authenticatedUserAtom, navBarExpandedAtom } from 'state'
import { myTwinAtom } from 'state/my-twin'
import { useFetchDraftedThreadCount } from 'hooks/use-email-responder'
import { COLORS } from 'app-constants'
import { SubscriptionStatus } from 'api/model'
import LogoutIcon from '@mui/icons-material/Logout'
import { trackNavigationWhileOnHomePage } from 'analytics/events'
import { useFetchConversationCounts } from 'hooks/use-conversation'
import { useUpdateKeyContext } from 'hooks/use-context-key'

export const animationDuration = '0.2s'

const NavContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  padding: 16px;
  background-color: #fff;
  z-index: 1000;
  user-select: none;
  height: 100%;
`

export const NavBarItem = styled(NavLink)`
  height: 40px;
  display: flex;
  align-items: center;
  border-radius: 8px;
  color: #999999;
  fill: #999999;
`

const HoverBackgroundNavBarItem = styled(NavBarItem)`
  &:hover {
    background-color: #e9f0fe;
    fill: white;
  }
`

export const ActiveBackgroundNavBarItem = styled(HoverBackgroundNavBarItem)`
  font-size: 14px;

  &:hover:not(.active) .MuiTypography-root {
    color: #3a7aff;
    font-weight: 500;
  }

  &:hover:not(.active) .count-badge {
    border: 1px solid #95b7ff;
  }

  &:hover {
    .MuiSvgIcon-root {
      fill: #3a7aff;
    }
  }

  &.active {
    background-color: #e9f0fe;
    color: #3a7aff;
    fill: white;

    .MuiSvgIcon-root {
      fill: #3a7aff;
    }
  }
`

export const IconContainer = styled(Box)`
  display: flex;
  align-items: center;
  flex: 0 0 40px;
  flex-direction: column;

  .MuiSvgIcon-root {
    font-size: 24px;
  }
`

export const AnimatedLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})<{ isExpanded: boolean }>`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  visibility: ${(props) => (props.isExpanded ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.isExpanded ? '1' : '0')};
  transition: visibility ${animationDuration}, opacity ${animationDuration} linear;
`

const NonLinkItemContainer = styled(Box)`
  display: flex;
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;

  &:hover {
    color: white;
    fill: white;
  }
`

// To avoid messing with the inner size of ProfilePhoto, we draw the border with a larger circle container
const AvatarContainer = styled.div`
  width: 36px;
  height: 36px;
  padding: 2px;
  border-radius: 9999px;
`

export const ButtonTooltipStyle = {
  padding: '8px',
}

export const LongTextTooltipStyle = {
  padding: '12px',
  background: COLORS.GRAY_1,
}

const NavBar = ({ user }: { user: User }) => {
  const theme = useTheme()

  const myTwin = useRecoilValue(myTwinAtom)

  // first we try to get nav bar state from recoil, then from local storage
  const isExpandedFromLocalStorage = localStorage.getItem('jelled_navBarExpanded')
  const [isExpanded, setIsExpanded] = useRecoilState(navBarExpandedAtom)

  // perform a fetch for gmail threads to get the number of drafted count in their gmail
  const { draftedThreadCount, refetchThreads } = useFetchDraftedThreadCount(myTwin?.id ?? '')

  useEffect(() => {
    // every hour, we should refetch the thread drafted count
    const interval = setInterval(() => {
      refetchThreads()
    }, 1000 * 60 * 60)
    return () => clearInterval(interval)
  }, [refetchThreads])

  // perform a fetch for unread conversations count
  const { conversationCounts } = useFetchConversationCounts()

  useEffect(() => {
    if (isExpandedFromLocalStorage !== null) {
      setIsExpanded(JSON.parse(isExpandedFromLocalStorage))
    }
  }, [isExpandedFromLocalStorage, setIsExpanded])

  const jelledIconRef = useRef<HTMLDivElement | null>(null)

  const [isProfilePreviewOpen, setIsProfilePreviewOpen] = useState(false)
  const profileContainer = useRef<HTMLDivElement | null>(null)
  const onProfileItemClicked = () => {
    setIsProfilePreviewOpen(true)
  }

  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false)
  const [, setAuthToken] = useAuthToken()
  const [, setActiveOrganization] = useActiveOrganization()
  const updateKey = useUpdateKeyContext()

  const onLogoutClicked = () => {
    setIsProfilePreviewOpen(false)
    setIsLogoutDialogOpen(true)
  }

  const onLogoutConfimed = () => {
    setAuthToken(null)
    setActiveOrganization(null)
    // clear recoil state by updating the key passed into <RecoilRoot />
    updateKey()

    // clear session storage
    sessionStorage.clear()

    setIsLogoutDialogOpen(false)
  }

  // write a helper function that takes in draftedThreadCount and returns a count but shows numbers over 1000 as 1k and over 10000 as 10k
  // this is to prevent the number from overflowing the badge
  const getDraftedThreadCount = (draftedThreadCount: number) => {
    if (draftedThreadCount < 1000) {
      return draftedThreadCount
    } else if (draftedThreadCount < 10000) {
      return `${Math.floor(draftedThreadCount / 1000)}k+`
    } else {
      // if the number is greater than 10k, we should return that number divided by 10000 and rounded to the nearest whole number
      return `${Math.round(draftedThreadCount / 1000)}k+`
    }
  }

  const isSubscriptionCancelled = user.subscription?.status === SubscriptionStatus.canceled

  // GA tracking
  const isOnInsightsPage = useMatch('/')

  // if we're still in onboarding route, don't render the nav bar
  if (window.location.pathname.includes('onboarding')) {
    return null
  }

  return (
    <>
      <NavContainer
        className="noprint"
        style={{
          pointerEvents: 'auto',
          display: 'block',
          borderRight: '1px solid #DDDDDD',
        }}>
        <Stack
          width={isExpanded ? '200px' : '40px'}
          spacing="12px"
          style={{
            transition: `width ${animationDuration}`,
          }}>
          {isExpanded ? (
            <Stack height="40px" alignItems="center" justifyContent="space-between" direction="row" mb={3}>
              <Stack
                direction="row"
                alignItems="center"
                onClick={() => {
                  window.open('https://jelled.ai', '_blank', 'noopener noreferrer')
                }}
                sx={{
                  cursor: 'pointer',
                }}>
                <IconContainer ref={jelledIconRef}>
                  <AppIcon />
                </IconContainer>

                <JelledText
                  style={{
                    width: '85px',
                    height: '30px',
                    visibility: isExpanded ? 'visible' : 'hidden',
                    opacity: isExpanded ? '1' : '0',
                    transition: `visibility ${animationDuration}, opacity ${animationDuration} linear`,
                  }}
                />
              </Stack>

              <IconButton
                onClick={() => {
                  setIsExpanded(!isExpanded)
                  localStorage.setItem('jelled_navBarExpanded', JSON.stringify(!isExpanded))
                }}>
                {isExpanded ? (
                  <FirstPageSharpIcon
                    sx={{
                      color: COLORS.GRAY_3,
                    }}
                  />
                ) : (
                  <LastPageSharpIcon
                    sx={{
                      color: COLORS.GRAY_3,
                    }}
                  />
                )}
              </IconButton>
            </Stack>
          ) : (
            <IconButton
              onClick={() => {
                setIsExpanded(!isExpanded)
                localStorage.setItem('jelled_navBarExpanded', JSON.stringify(!isExpanded))
              }}
              style={{
                marginBottom: isExpanded ? 0 : '24px',
              }}>
              {isExpanded ? (
                <FirstPageSharpIcon
                  sx={{
                    color: COLORS.GRAY_3,
                  }}
                />
              ) : (
                <LastPageSharpIcon
                  sx={{
                    color: COLORS.GRAY_3,
                  }}
                />
              )}
            </IconButton>
          )}

          {!isSubscriptionCancelled && (
            <Tooltip
              title="Insights"
              placement="right"
              componentsProps={{
                tooltip: {
                  sx: {
                    ...ButtonTooltipStyle,
                    visibility: !isExpanded ? 'visible' : 'hidden',
                  },
                },
              }}>
              <ActiveBackgroundNavBarItem
                to="/"
                onClick={() => {
                  // GA tracking
                  if (isOnInsightsPage) {
                    trackNavigationWhileOnHomePage('/')
                  }
                }}
                style={{
                  marginBottom: '12px',
                  marginTop: 0,
                }}>
                <IconContainer ref={jelledIconRef}>
                  {window.location.pathname === '/' ? <TipsAndUpdatesIcon /> : <TipsAndUpdatesOutlinedIcon />}
                </IconContainer>

                <AnimatedLabel
                  isExpanded={isExpanded}
                  variant={window.location.pathname === '/' ? 'pSO' : 'body0'}
                  sx={{
                    color: window.location.pathname === '/' ? COLORS.JELLED_LIGHT_BLUE : COLORS.GRAY_3,
                  }}>
                  Insights
                </AnimatedLabel>
              </ActiveBackgroundNavBarItem>
            </Tooltip>
          )}

          {!isSubscriptionCancelled && (
            <Tooltip
              title="Trends"
              placement="right"
              componentsProps={{
                tooltip: {
                  sx: {
                    ...ButtonTooltipStyle,
                    visibility: !isExpanded ? 'visible' : 'hidden',
                  },
                },
              }}>
              <ActiveBackgroundNavBarItem
                to="/trends"
                onClick={() => {
                  // GA tracking
                  if (isOnInsightsPage) {
                    trackNavigationWhileOnHomePage('/trends')
                  }
                }}
                style={{
                  marginBottom: '4px',
                  marginTop: 0,
                }}>
                <IconContainer ref={jelledIconRef}>
                  <AutoGraphIcon />
                </IconContainer>

                <AnimatedLabel
                  isExpanded={isExpanded}
                  variant={window.location.pathname.includes('trends') ? 'pSO' : 'body0'}
                  sx={{
                    color: window.location.pathname.includes('trends') ? COLORS.JELLED_LIGHT_BLUE : COLORS.GRAY_3,
                  }}>
                  Trends
                </AnimatedLabel>
              </ActiveBackgroundNavBarItem>
            </Tooltip>
          )}

          {!isSubscriptionCancelled && (
            <Tooltip
              title="Emails"
              placement="right"
              componentsProps={{
                tooltip: {
                  sx: {
                    ...ButtonTooltipStyle,
                    visibility: !isExpanded ? 'visible' : 'hidden',
                  },
                },
              }}>
              <ActiveBackgroundNavBarItem
                to="/emails"
                onClick={() => {
                  // GA tracking
                  if (isOnInsightsPage) {
                    trackNavigationWhileOnHomePage('/emails')
                  }
                }}>
                <IconContainer>
                  {window.location.pathname === '/emails' ? (
                    <EmailIcon
                      style={{
                        width: '24px',
                        height: '24px',
                      }}
                    />
                  ) : (
                    <EmailOutlinedIcon
                      style={{
                        width: '24px',
                        height: '24px',
                      }}
                    />
                  )}
                </IconContainer>

                <AnimatedLabel
                  isExpanded={isExpanded}
                  sx={{
                    justifyContent: 'space-between',
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    mr: 1,
                  }}>
                  <Typography
                    component="span"
                    width="fit-content"
                    display="inline-block"
                    variant={window.location.pathname === '/emails' ? 'pSO' : 'body0'}>
                    Emails
                  </Typography>
                  {draftedThreadCount && draftedThreadCount !== 0 ? (
                    <Tooltip title="This count is for the number of drafts that you have not yet sent out.">
                      <Stack
                        component="span"
                        className="count-badge"
                        borderRadius="6px"
                        px={1}
                        display="inline-block"
                        alignItems="center"
                        width="fit-content"
                        justifyContent="center"
                        sx={{
                          border:
                            window.location.pathname === '/emails'
                              ? '1px solid #95b7ff'
                              : `1px solid ${COLORS.JELLED_MID_BLUE_10_PERCENT}`,
                        }}>
                        <Typography
                          component="span"
                          variant="pXS"
                          width="100%"
                          sx={{
                            color: COLORS.JELLED_PRIMARY_BLUE,
                          }}>
                          {getDraftedThreadCount(draftedThreadCount)}
                        </Typography>
                      </Stack>
                    </Tooltip>
                  ) : null}
                </AnimatedLabel>
              </ActiveBackgroundNavBarItem>
            </Tooltip>
          )}

          {!isSubscriptionCancelled && (
            <Tooltip
              title="JelledBots"
              placement="right"
              componentsProps={{
                tooltip: {
                  sx: {
                    ...ButtonTooltipStyle,
                    visibility: !isExpanded ? 'visible' : 'hidden',
                  },
                },
              }}>
              <ActiveBackgroundNavBarItem
                to="/jelledbots"
                onClick={() => {
                  // GA tracking
                  if (isOnInsightsPage) {
                    trackNavigationWhileOnHomePage('/jelledbots')
                  }
                }}
                style={{
                  marginBottom: '4px',
                }}>
                <IconContainer>
                  {window.location.pathname === '/jelledbots' ? (
                    <Groups3Icon style={{ width: '20px', height: '20px' }} />
                  ) : (
                    <Groups3OutlinedIcon style={{ width: '20px', height: '20px' }} />
                  )}
                </IconContainer>

                <AnimatedLabel
                  isExpanded={isExpanded}
                  sx={{
                    justifyContent: 'space-between',
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    mr: 1,
                  }}>
                  <Typography
                    component="span"
                    width="fit-content"
                    display="inline-block"
                    variant={window.location.pathname === '/jelledbots' ? 'pSO' : 'body0'}>
                    JelledBots
                  </Typography>

                  {conversationCounts !== 0 ? (
                    <Tooltip title="This count is for the number of unread reviews in JelledBots.">
                      <Stack
                        component="span"
                        className="count-badge"
                        borderRadius="6px"
                        px={1}
                        display="inline-block"
                        alignItems="center"
                        width="fit-content"
                        justifyContent="center"
                        sx={{
                          border:
                            window.location.pathname === '/jelledbots'
                              ? '1px solid #95b7ff'
                              : `1px solid ${COLORS.JELLED_MID_BLUE_10_PERCENT}`,
                        }}>
                        <Typography
                          component="span"
                          variant="pXS"
                          width="100%"
                          sx={{
                            color: COLORS.JELLED_PRIMARY_BLUE,
                          }}>
                          {conversationCounts}
                        </Typography>
                      </Stack>
                    </Tooltip>
                  ) : null}
                </AnimatedLabel>
              </ActiveBackgroundNavBarItem>
            </Tooltip>
          )}

          {isExpanded ? (
            <Typography
              variant="pXS"
              sx={{
                color: COLORS.GRAY_4,
              }}>
              CONFIGURATION
            </Typography>
          ) : (
            <Stack height="16px" width="40px" justifyContent="center" alignItems="center">
              <Stack
                sx={{
                  height: '1px',
                  width: '40px',
                  background: COLORS.GRAY_6,
                }}
              />
            </Stack>
          )}

          {!isSubscriptionCancelled && (
            <Tooltip
              title="My Twin"
              placement="right"
              componentsProps={{
                tooltip: {
                  sx: {
                    ...ButtonTooltipStyle,
                    visibility: !isExpanded ? 'visible' : 'hidden',
                  },
                },
              }}>
              <ActiveBackgroundNavBarItem
                to={'/my-twin'}
                onClick={() => {
                  // GA tracking
                  if (isOnInsightsPage) {
                    trackNavigationWhileOnHomePage('/my-twin')
                  }
                }}>
                <IconContainer>
                  <SupportAgentOutlinedIcon />
                </IconContainer>

                <AnimatedLabel
                  isExpanded={isExpanded}
                  variant={window.location.pathname.includes('/my-twin') ? 'pSO' : 'body0'}>
                  My Twin
                </AnimatedLabel>
              </ActiveBackgroundNavBarItem>
            </Tooltip>
          )}

          <Tooltip
            title="Settings"
            placement="right"
            componentsProps={{
              tooltip: {
                sx: {
                  ...ButtonTooltipStyle,
                  visibility: !isExpanded ? 'visible' : 'hidden',
                },
              },
            }}>
            <ActiveBackgroundNavBarItem
              to="/organization"
              onClick={() => {
                // GA tracking
                if (isOnInsightsPage) {
                  trackNavigationWhileOnHomePage('/organization')
                }
              }}>
              <IconContainer>
                {window.location.pathname.includes('/organization') ? <SettingsIcon /> : <SettingsOutlinedIcon />}
              </IconContainer>

              <AnimatedLabel
                isExpanded={isExpanded}
                variant={window.location.pathname.includes('/organization') ? 'pSO' : 'body0'}>
                Settings
              </AnimatedLabel>
            </ActiveBackgroundNavBarItem>
          </Tooltip>

          {user.is_system_admin && !isSubscriptionCancelled && (
            <Tooltip
              title="Admin"
              placement="right"
              componentsProps={{
                tooltip: {
                  sx: {
                    ...ButtonTooltipStyle,
                    visibility: !isExpanded ? 'visible' : 'hidden',
                  },
                },
              }}>
              <ActiveBackgroundNavBarItem
                to="/admin"
                onClick={() => {
                  // GA tracking
                  if (isOnInsightsPage) {
                    trackNavigationWhileOnHomePage('/admin')
                  }
                }}>
                <IconContainer>
                  {window.location.pathname.includes('/admin') ? (
                    <AdminPanelSettingsIcon />
                  ) : (
                    <AdminPanelSettingsOutlinedIcon />
                  )}
                </IconContainer>

                <AnimatedLabel
                  isExpanded={isExpanded}
                  variant={window.location.pathname.includes('/admin') ? 'pSO' : 'body0'}>
                  Admin
                </AnimatedLabel>
              </ActiveBackgroundNavBarItem>
            </Tooltip>
          )}

          <ProfileItem user={user} isExpanded={isExpanded} ref={profileContainer} onClick={onProfileItemClicked} />
        </Stack>
      </NavContainer>

      <FloatingProfilePreview
        isExpanded={isExpanded}
        isOpen={isProfilePreviewOpen}
        anchorEl={profileContainer.current}
        onClose={() => setIsProfilePreviewOpen(false)}
        onLogoutClicked={onLogoutClicked}
      />

      <Dialog open={isLogoutDialogOpen}>
        <DialogTitle>Are you sure you want to logout?</DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              setIsLogoutDialogOpen(false)
            }}
            sx={{
              color: getColorForDarkMode(theme),
            }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={onLogoutConfimed}>
            Yes, logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export const ProfileItem = forwardRef(
  (props: { user: User; isExpanded: boolean; onClick: () => void }, ref: Ref<HTMLDivElement | null>) => {
    const isActive = useMatch('/profile')

    if (!props.isExpanded) {
      return (
        <NonLinkItemContainer
          onClick={props.onClick}
          sx={{
            position: 'fixed',
            bottom: '32px',
          }}>
          <IconContainer ref={ref}>
            <AvatarContainer
              style={{
                cursor: 'pointer',
              }}>
              <ProfilePhoto src={props.user.picture} size="32px" name={props.user.name ?? props.user.email} />
            </AvatarContainer>
          </IconContainer>
        </NonLinkItemContainer>
      )
    }

    return (
      <NonLinkItemContainer
        onClick={props.onClick}
        sx={{
          backgroundColor: COLORS.GRAY_8,
          position: 'fixed',
          bottom: '32px',
          left: '16px',
          width: '200px',
          borderRadius: '8px',
          border: '1px solid #BBB',
          boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.15)',
          px: 2,
          py: '10px',
          height: '52px',
        }}>
        <IconContainer ref={ref}>
          <AvatarContainer style={{ backgroundColor: isActive ? '#115DFC' : 'transparent' }}>
            <ProfilePhoto src={props.user.picture} size="32px" name={props.user.name ?? props.user.email} />
          </AvatarContainer>
        </IconContainer>

        <AnimatedLabel
          isExpanded={props.isExpanded}
          variant="body0"
          ml={1}
          sx={{
            color: COLORS.GRAY_0,
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
          {props.user.name ?? props.user.email}
        </AnimatedLabel>
      </NonLinkItemContainer>
    )
  }
)

const FloatingProfilePreview = ({
  isExpanded,
  isOpen,
  anchorEl,
  onClose,
  onLogoutClicked,
}: {
  isExpanded: boolean
  isOpen: boolean
  anchorEl?: null | VirtualElement | (() => VirtualElement)
  onClose: () => void
  onLogoutClicked: () => void
}) => {
  const user = useRecoilValue(authenticatedUserAtom)

  return (
    <Popper
      open={isOpen}
      anchorEl={anchorEl}
      placement="right"
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [-30, isExpanded ? 176 : 32],
          },
        },
      ]}>
      <ClickAwayListener onClickAway={onClose}>
        <Stack
          width="271px"
          p="20px"
          borderRadius="8px"
          sx={{
            gap: '16px',
            background: COLORS.PURE_WHITE,
            boxShadow: '0px 0px 20px 0px rgba(0, 0, 0, 0.08)',
          }}>
          {user && <Typography variant="pS">{user.email}</Typography>}
          <Button
            variant="outlined"
            color="secondary"
            size="medium"
            onClick={onLogoutClicked}
            startIcon={<LogoutIcon />}
            sx={{
              width: '129px',
              backgroundColor: COLORS.PURE_WHITE,
            }}>
            Log out
          </Button>
        </Stack>
      </ClickAwayListener>
    </Popper>
  )
}

export default NavBar
